import { useRef, useState } from "react";

// Mui
import { Box, Typography, IconButton, Tooltip, Skeleton } from "@mui/material";

// Ability
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";

// Components
import ProText from "components/Pro/ProText";
import CustomIcon from "components/CustomIcon";
import { EntityWrapper } from "components/EntityWrapper";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackBar, SnackBar } from "components/SnackBar";
import { FieldHintButton } from "components/FieldHintButton";
import ProTooltip, { ProTooltipTitle } from "components/Pro/Tooltip";
import { ButtonWithCounter } from "components/Buttons/ButtonWithCounter";
import { CircledButton } from "pages/ProjectDashboard/styled-components";

// Store
import { getProject } from "store/slice/Project/projectData/ProjectSlice";
import { fetchPersonSet } from "store/slice/Project/peopleSet/peopleSetSlice";
import { fetchCompanySet } from "store/slice/Project/companiesSet/companiesSetSlice";

// Utils
import icons from "enums/icons";
import { unwrapResult } from "@reduxjs/toolkit";
import { useAppSelector, useAppDispatch } from "app/hooks";
import { convertProjectPerson, getCompaniesList, getPeopleList } from "pages/ProjectDashboard/adapters/formattedData";
import { convertProjectCompany } from "../../adapters/formattedData";
import { EntityRow } from "components/EntityRow";

export type TEntityCompany = ReturnType<typeof getCompaniesList>[0];
export type TEntityPerson = ReturnType<typeof getPeopleList>[0];

export const RowHead = ({ title }: { title: JSX.Element }) => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                pb: 0.5,
                mt: 1.5,
            }}
        >
            <Box sx={{ flex: "1 1 45%" }}></Box>
            <Typography
                sx={{
                    flex: "1 1 55%",
                    fontSize: 10,
                    letterSpacing: "1px",
                    paddingLeft: 1,
                    color: "text.disabled",
                    textTransform: "uppercase",
                    textAlign: "right",
                }}
            >
                {title}
            </Typography>
        </Box>
    );
};

export const Row = ({ term, subTerm, value }: { term?: string; subTerm?: string; value?: string }) => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #DADEE5",
                py: 1,
            }}
        >
            <Box sx={{ flex: "1 1 45%", borderRight: "1px solid #DADEE5" }}>
                <Typography sx={{ fontSize: 12, textTransform: "capitalize" }}>{term}</Typography>
                {subTerm && <Typography sx={{ fontSize: 10 }}>{subTerm}</Typography>}
            </Box>
            <Typography
                sx={{
                    color: value ? "text.primary" : "text.disabled",
                    flex: "1 1 55%",
                    fontSize: 10,
                    letterSpacing: "0.43px",
                    paddingLeft: 1,
                }}
            >
                {value || "Unknown"}
            </Typography>
        </Box>
    );
};

const ROWHEIGHT = 43;
const EntityList = ({
    data,
    title,
    dataType,
    onClickRelatedButton,
}: {
    data: (TEntityCompany | TEntityPerson)[];
    title?: string;
    dataType: "company" | "people";
    onClickRelatedButton: (id: number, type: "insight" | "searchAi" | "brief" | "company" | "people") => void;
}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const ability = useAbility(AbilityContext);

    // Since design-wise we always show the link button for Briefs, this permissions is a workaround until we have a propper permission
    const hasProPermission = ability.can("see", "SAAS_LD_PROJECT_BRIEF_CREATE") || !ability.can("see", "LD_FREE");

    const { data: projectData } = useAppSelector(getProject);

    const [openProTooltipRowId, setOpenProTooltipRowId] = useState(0);
    const [entityListHeight, setEntityListHeight] = useState(ROWHEIGHT * 2);
    const [selectedEntity, setSelectedEntity] = useState<null | TEntityCompany | TEntityPerson>(null);

    const isExpanded = entityListHeight > 2 * ROWHEIGHT;
    const wrapperRef = useRef<HTMLElement | null | undefined>(null);

    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();

    const handleShowList = async (event: React.MouseEvent<HTMLElement>, data: TEntityCompany | TEntityPerson) => {
        if (!hasProPermission) return;
        wrapperRef.current = event.currentTarget.parentElement?.parentElement;
        handleOpenSnackBar(event);

        if (dataType === "people") {
            const set = unwrapResult(
                await dispatch(fetchPersonSet({ projectId: projectData.data.id, path: data.ref }))
            );

            const people = set?.children.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...(prevVal as TEntityPerson), people }));
        }

        if (dataType === "company") {
            const set = unwrapResult(
                await dispatch(fetchCompanySet({ projectId: projectData.data.id, path: data.ref }))
            );

            const companies = set?.children.map((p: any) => convertProjectCompany(p.data));
            setSelectedEntity((prevVal) => ({ ...(prevVal as TEntityCompany), companies }));
        }
    };

    const LoadingState = () => (
        <>
            <Skeleton variant="rectangular" animation="wave" height={25} sx={{ mb: 0.5 }} />
            <Skeleton variant="rectangular" animation="wave" height={25} sx={{ mb: 0.5 }} />
            <Skeleton variant="rectangular" animation="wave" height={25} sx={{ mb: 0.5 }} />
            <Skeleton variant="rectangular" animation="wave" height={25} sx={{ mb: 0.5 }} />
            <Skeleton variant="rectangular" animation="wave" height={25} />
        </>
    );

    return (
        <Box position="relative">
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.background.grey,
                    width: "100%",
                    height: entityListHeight + 27 + 7 * 8,
                    position: "absolute",
                    zIndex: 3,
                    padding: theme.spacing(2.5, 2.5, 0, 2.5),
                    borderRadius: theme.spacing(1),
                    boxShadow: "0 1px 3px 0 #0000001A, 0 1px 2px 0 #0000000F",
                    transition: "height 250ms",
                })}
            >
                <Box
                    sx={{
                        width: "calc(100% - 40px)",
                        position: "absolute",
                    }}
                >
                    <Box display="flex" alignItems="center" mb={1.4}>
                        <Typography sx={{ fontSize: 18, marginRight: 1 }}>{title}</Typography>
                        <FieldHintButton iconSize={22} iconColor="info.main" popoverMaxWidth={360}>
                            <FormattedMessage
                                id={
                                    hasProPermission
                                        ? `project.dashboard.${dataType}List.hint`
                                        : `project.dashboard.${dataType}List.hint.goPro`
                                }
                            />
                        </FieldHintButton>
                    </Box>
                    <Box
                        sx={{
                            height: entityListHeight,
                            overflowY: isExpanded ? "scroll" : "hidden",
                            transition: "height 250ms",
                        }}
                    >
                        {!data.length && <FormattedMessage id={`project.dashboard.${dataType}List.noData`} />}
                        {data
                            .filter((d) => !d?.isClone)
                            .map((d) => {
                                return (
                                    d && (
                                        <EntityWrapper
                                            key={d.id}
                                            size="small"
                                            sx={{ display: d.shouldHide ? "none" : "flex" }}
                                        >
                                            <EntityRow
                                                data={d}
                                                relatedButton={
                                                    <Tooltip
                                                        arrow
                                                        placement="bottom-start"
                                                        title={intl.formatMessage({
                                                            id: "dashboard.insights.linkButton.tooltip",
                                                        })}
                                                    >
                                                        <CircledButton
                                                            size={22}
                                                            component="button"
                                                            isDisabled={!hasProPermission}
                                                            onClick={() =>
                                                                hasProPermission && onClickRelatedButton(d.id, dataType)
                                                            }
                                                            color={d.shouldHighlight ? "hovered" : "hovered2"}
                                                        >
                                                            <CustomIcon icon={icons.link} sx={{ fontSize: 10 }} />
                                                        </CircledButton>
                                                    </Tooltip>
                                                }
                                                countButton={
                                                    <Box display="flex" alignItems="center">
                                                        {!hasProPermission && (
                                                            <ProTooltip
                                                                open={d.id === openProTooltipRowId}
                                                                title={
                                                                    <ProTooltipTitle
                                                                        onClose={() => setOpenProTooltipRowId(0)}
                                                                    />
                                                                }
                                                            >
                                                                <Box sx={{ mx: 1 }}>
                                                                    <ProText
                                                                        onClick={() => setOpenProTooltipRowId(d.id)}
                                                                    />
                                                                </Box>
                                                            </ProTooltip>
                                                        )}
                                                        <ButtonWithCounter
                                                            count={d.count}
                                                            isDisabled={!hasProPermission || !d.count}
                                                            shouldShowIcon={selectedEntity?.id === d.id}
                                                            callback={(ev) => handleShowList(ev, d)}
                                                        />
                                                    </Box>
                                                }
                                            />
                                        </EntityWrapper>
                                    )
                                );
                            })}
                    </Box>
                    {data.length >= 3 ? (
                        <Box display="flex" justifyContent="center" mt={0.5}>
                            <IconButton
                                size="small"
                                sx={{ width: "100%" }}
                                onClick={() =>
                                    setEntityListHeight((currentVal) =>
                                        currentVal === 2 * ROWHEIGHT
                                            ? Math.min((data.length || 1) * ROWHEIGHT, 6 * ROWHEIGHT)
                                            : 2 * ROWHEIGHT
                                    )
                                }
                            >
                                <CustomIcon
                                    sx={{ fontSize: 12, color: "button.active.to" }}
                                    icon={isExpanded ? icons.chevronUpThin : icons.chevronDownThin}
                                />
                            </IconButton>
                        </Box>
                    ) : undefined}
                </Box>
            </Box>

            <SnackBar
                maxWidth={wrapperRef.current?.getBoundingClientRect().width || 400}
                shouldOpen={shouldOpenSnackBar}
                anchorElement={popOverAnchorEl}
                title={<strong>{selectedEntity?.name}</strong>}
                handleInfoClose={(ev) => {
                    handleCloseSnackBar(ev);
                    setTimeout(() => {
                        setSelectedEntity(null);
                    }, 350);
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                {...(wrapperRef.current
                    ? {
                          anchorPosition: {
                              top: wrapperRef.current?.getBoundingClientRect().bottom + 5,
                              left:
                                  wrapperRef.current?.getBoundingClientRect().left +
                                  wrapperRef.current?.getBoundingClientRect().width / 2,
                          },
                      }
                    : {})}
                content={
                    dataType === "company" ? (
                        <>
                            <RowHead title={<FormattedMessage id="project.dashboard.popup.header.company.subterm" />} />
                            {!Boolean((selectedEntity as TEntityCompany)?.companies?.length) ? (
                                <LoadingState />
                            ) : (
                                <Box sx={{ px: 2, maxHeight: 300, overflowY: "scroll" }}>
                                    {(selectedEntity as TEntityCompany)?.companies?.map((c) => (
                                        <Row key={c.id} term={c.name} value={c.capitalStructure ?? ""} />
                                    ))}
                                </Box>
                            )}
                        </>
                    ) : (
                        <>
                            <RowHead title={<FormattedMessage id="project.dashboard.popup.header.people.subterm" />} />
                            {!Boolean((selectedEntity as TEntityPerson)?.people?.length) ? (
                                <LoadingState />
                            ) : (
                                <Box sx={{ px: 2, maxHeight: 300, overflowY: "scroll" }}>
                                    {(selectedEntity as TEntityPerson).people.map((p) => (
                                        <Row key={p.id} term={p.name} subTerm={p.jobTitle} value={p.company ?? ""} />
                                    ))}
                                </Box>
                            )}
                        </>
                    )
                }
            />
        </Box>
    );
};

export default EntityList;
